import { createTheme } from '@mui/material/styles';

// ── Roamoir design tokens ────────────────────────────────────────────────────
//
// Structured after ardent-scholar's theme — same rigor, deliberately NOT the
// same look. Scholar is navy-and-gold: academic, institutional. Roamoir is a
// travel journal: warm, physical, unhurried (roamoir-mvp/CLAUDE.md). Borrowing
// scholar's palette would put an Ardent Academy face on a different product.
//
// Values below are the SAME literals the mobile app uses in
// roamoir-mvp/src/theme.ts. Both clients render the same trips; a colour that
// drifts between them is a bug, not a variation.
//
// Palette rationale:
//   Primary  — Forest (#1A3A2A): the colour of the app's own output surface,
//              so brand and content share one ground
//   Accent   — Ember (#C8522A): sparing, for the single live thing on a screen
//   Surface  — Warm paper (#F5F2EE): a journal page, not a dashboard
//   Text     — Warm dark brown (#2C1F14): softer than black, reads as ink
//
// Typography:
//   UI       — system sans. Navigation should disappear.
//   Voice    — Georgia italic, and ONLY for Roamoir-generated prose. This is a
//              product rule, not a preference: generated text must always be
//              visually separable from what the traveller wrote. Use the
//              `serifVoice` export, never an ad-hoc fontFamily.
//
// Shape:
//   12px — matches the mobile app's radius.md. Softer than scholar's 8px;
//   a journal has rounded corners, a dashboard has square ones.
//
// Shadows:
//   Tinted with the ink brown rather than black or navy, so elevation reads as
//   paper lifting off paper instead of a UI panel floating.

const FOREST = '#1A3A2A';
const EMBER = '#C8522A';
const PAPER = '#F5F2EE';
const INK = '#2C1F14';

/** Trip and place lifecycle. Same four states and colours as the mobile app. */
export const statusColors = {
  going: '#9E9E9E',
  here: EMBER,
  curating: '#D97706',
  done: '#16A34A',
} as const;

/** The full-screen surface Roamoir's generated output renders on. */
export const outputSurface = {
  bg: FOREST,
  text: '#E8F4EC',
  muted: '#7EA892',
} as const;

/**
 * Roamoir's voice. Apply as sx={{ ...serifVoice }} to generated prose only —
 * never to UI chrome, labels, or anything the traveller typed themselves.
 */
export const serifVoice = {
  fontFamily: 'Georgia, "Times New Roman", serif',
  fontStyle: 'italic' as const,
};

/**
 * Display serif for big headlines only — hero and page titles. Distinct from
 * serifVoice: that is Georgia italic for what Roamoir writes; this is the
 * editorial face of the brand, set large.
 */
export const displayFont = '"Playfair Display", Georgia, "Times New Roman", serif';

/** Kept for existing call sites that only need the family. */
export const serifFont = 'Georgia, "Times New Roman", serif';

const shadow = (y: number, blur: number, alpha: number) =>
  `0 ${y}px ${blur}px rgba(44,31,20,${alpha})`;

/** Corner radii. Photos and cards are rounder than controls. */
export const radius = { control: 14, card: 20, photo: 18, pill: 999 } as const;

/** Elevation. Warm-tinted, soft and wide — the reference look, not Material's. */
export const elevation = {
  card: '0 1px 2px rgba(44,31,20,0.04), 0 8px 24px rgba(44,31,20,0.06)',
  raised: '0 2px 6px rgba(44,31,20,0.06), 0 16px 40px rgba(44,31,20,0.10)',
  photo: '0 10px 30px rgba(14,36,25,0.18)',
} as const;

export const theme = createTheme({
  palette: {
    primary: {
      main: FOREST,
      light: '#2C5643',
      dark: '#0E2419',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: EMBER,
      light: '#D97A56',
      dark: '#9C3E1F',
      contrastText: '#FFFFFF',
    },
    background: {
      default: PAPER,
      paper: '#FFFFFF',
    },
    text: {
      primary: INK,
      secondary: '#7A6A5A',
    },
    divider: '#DDD8D0',
    error: { main: '#A33B22' },
    warning: { main: statusColors.curating },
    success: { main: statusColors.done },
    // Warm-biased neutrals. A pure grey next to this paper reads as dirty.
    grey: {
      50: '#FAF8F5',
      100: '#F5F2EE',
      200: '#E8E2D9',
      300: '#DDD8D0',
      400: '#BFB5A8',
      500: '#9C9085',
      600: '#7A6A5A',
      700: '#5C4F42',
      800: '#40362C',
      900: INK,
    },
  },

  typography: {
    // Plus Jakarta Sans: a geometric sans with warmth, so the UI reads modern
    // without the default-system look. Georgia stays the voice (serifVoice).
    fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: { fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.08 },
    h2: { fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.12 },
    h3: { fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.18 },
    h4: { fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.22 },
    h5: { fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    body1: { lineHeight: 1.65 },
    body2: { lineHeight: 1.6 },
    button: { fontWeight: 600, letterSpacing: '0.01em', textTransform: 'none' },
    overline: { letterSpacing: '0.12em', fontWeight: 600 },
    caption: { letterSpacing: '0.01em' },
  },

  // 14 for controls; cards and photos go larger (see `radius`).
  shape: { borderRadius: 14 },

  shadows: [
    'none',
    shadow(1, 3, 0.05),
    shadow(2, 6, 0.06),
    shadow(4, 12, 0.07),
    shadow(6, 16, 0.08),
    shadow(8, 20, 0.09),
    shadow(10, 24, 0.1),
    shadow(12, 28, 0.1),
    shadow(14, 32, 0.11),
    shadow(16, 36, 0.11),
    shadow(18, 40, 0.12),
    shadow(20, 44, 0.12),
    shadow(22, 48, 0.13),
    shadow(24, 52, 0.13),
    shadow(26, 56, 0.14),
    shadow(28, 60, 0.14),
    shadow(30, 64, 0.15),
    shadow(32, 68, 0.15),
    shadow(34, 72, 0.16),
    shadow(36, 76, 0.16),
    shadow(38, 80, 0.17),
    shadow(40, 84, 0.17),
    shadow(42, 88, 0.18),
    shadow(44, 92, 0.18),
    shadow(46, 96, 0.19),
  ],

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: PAPER,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 10, paddingTop: 9, paddingBottom: 9, paddingLeft: 20, paddingRight: 20 },
        contained: { '&:hover': { backgroundColor: '#2C5643' } },
        outlined: {
          borderColor: 'rgba(26,58,42,0.28)',
          '&:hover': { borderColor: FOREST, backgroundColor: 'rgba(26,58,42,0.04)' },
        },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        // Lift, not lines: a soft warm shadow instead of a border.
        root: {
          border: 'none',
          boxShadow: elevation.card,
          borderRadius: radius.card,
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 12, backgroundImage: 'none' },
        elevation1: { boxShadow: shadow(2, 6, 0.06) },
        elevation2: { boxShadow: shadow(4, 12, 0.07) },
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'default' },
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #DDD8D0',
          backgroundImage: 'none',
        },
      },
    },

    MuiTextField: {
      defaultProps: { variant: 'outlined' },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#FFFFFF',
          '& fieldset': { borderColor: '#DDD8D0' },
          '&:hover fieldset': { borderColor: '#BFB5A8' },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 999 },
      },
    },

    MuiAlert: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: { borderRadius: 10 },
      },
    },

    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: {
        root: { color: EMBER, textAlign: 'left' },
      },
    },
  },
});
